import BaseAccordion from 'components/Accordions/BaseAccordion';
import DeviceStatusBadge from 'features/Integrations/components/DeviceStatusBadge/DeviceStatusBadge';
import { useState } from 'react';
import { Button } from 'react-bootstrap';
import { MdExpandLess, MdExpandMore } from 'react-icons/md';
import { useGetLicenseInfoQuery } from 'services/integrations/integrations.api';
import { deviceImageList, getDynamicDeviceIcon } from 'utils/devices/icon.utils';
import { getLicensePool } from '../../../../config/licensepools.config';
import { getDeviceLicenseLabel, getSystemLicenseLabel } from '../../../../utils/license.utils';
import ActionMenu from '../ActionMenu/ActionMenu';

const formatDeviceTypeLabel = (deviceType = '') => deviceType.replaceAll('_', ' ');

const getCategoryIcon = (categoryName) =>
  deviceImageList[categoryName] || deviceImageList['RESCALERT'];

const getSystemIcon = (systemType) => deviceImageList[systemType] || deviceImageList['RESCALERT'];

const hasDisplayValue = (value) => value !== null && value !== undefined && value !== '';

const getSystemDisplayId = (system = {}) => system.id ?? system.system_id;

const getDeviceDisplayId = (device = {}) => device.node_id ?? device.device_id ?? device.id;

const Accordion = ({
  deviceIndexMap,
  systemIndexMap,
  connectedSystemsHierarchy,
  openEditFormForSystemOrDevice,
  prepareSystemOrDeviceForDeletion,
  deviceRowIdEllipsisOpen,
  setDeviceRowIdEllipsisOpen,
  systemRowIdEllipsisOpen,
  setSystemRowIdEllipsisOpen,
}) => {
  const { data: licenseInfo = {} } = useGetLicenseInfoQuery();
  const [openRows, setOpenRows] = useState({});

  const toggleRow = (rowKey) => {
    setOpenRows((current) => ({
      ...current,
      [rowKey]: !current[rowKey],
    }));
  };

  const renderDeviceGroup = (deviceGroup, systemRowId) => {
    const groupRowKey = `${systemRowId}:${deviceGroup.id}`;
    const isOpen = !!openRows[groupRowKey];

    return (
      <div
        key={deviceGroup.id}
        className="border border-secondary border-opacity-75 rounded-2 overflow-hidden bg-primary"
      >
        <Button
          type="button"
          variant="link"
          className="w-100 border-0 text-white text-start text-decoration-none px-3 py-3 bg-transparent rounded-0"
          onClick={() => toggleRow(groupRowKey)}
          aria-expanded={isOpen}
          aria-controls={`${groupRowKey}-devices`}
        >
          <div className="d-flex align-items-center justify-content-between gap-3">
            <div className="d-flex align-items-center gap-3 min-w-0">
              <img
                src={deviceImageList[deviceGroup.deviceType] || deviceImageList['RESCALERT']}
                alt={deviceGroup.deviceType}
                width={'30px'}
                className="flex-shrink-0"
              />
              <span className="fw-semibold text-truncate">
                {formatDeviceTypeLabel(deviceGroup.deviceType)}
              </span>
            </div>
            <div className="d-flex align-items-center gap-3 flex-shrink-0">
              <span>{deviceGroup.devices.length}</span>
              {isOpen ? <MdExpandLess size={20} /> : <MdExpandMore size={20} />}
            </div>
          </div>
        </Button>

        {isOpen && (
          <div id={`${groupRowKey}-devices`} className="px-3 pb-3">
            {deviceGroup.devices.length > 0 ? (
              deviceGroup.devices.map((device, deviceIndex) => (
                <div
                  key={device.id}
                  className={`py-3 bg-primary ${
                    deviceIndex === deviceGroup.devices.length - 1
                      ? ''
                      : 'border-bottom border-secondary'
                  }`}
                >
                  <div className="d-flex align-items-start justify-content-between gap-3">
                    <div className="d-flex align-items-start gap-3 min-w-0">
                      {deviceGroup.deviceType === 'Stage' ? (
                        <div className="bg-light p-1 flex-shrink-0" style={{ marginLeft: '-3px' }}>
                          <img
                            src={getDynamicDeviceIcon(device)}
                            alt={device?.system_type}
                            style={{ width: '30px', height: '49px' }}
                          />
                        </div>
                      ) : (
                        <img
                          src={getDynamicDeviceIcon(device)}
                          alt={device?.system_type}
                          width={'30px'}
                          className="flex-shrink-0"
                        />
                      )}
                      <div className="min-w-0">
                        <p className="mb-1 fw-bold">
                          {device?.alias || device?.monitoring_location_name || device?.name}
                        </p>
                        {hasDisplayValue(getDeviceDisplayId(device)) && (
                          <p className="mb-1">ID: {getDeviceDisplayId(device)}</p>
                        )}
                        <p className="mb-1 text-light opacity-75">
                          {device?.static_id || device?.name}
                        </p>
                        <p className="mb-1">
                          Type:{' '}
                          {`${device?.system_type} ${formatDeviceTypeLabel(device?.device_type)}`}
                        </p>
                        {getLicensePool(device?.system_type, device?.device_type) !== 'virtual' && (
                          <p className="mb-1">
                            License Info:{' '}
                            {getDeviceLicenseLabel(device, licenseInfo, deviceIndexMap)}
                          </p>
                        )}
                        <p className="mb-2">
                          Date Integrated: {new Date(device?.date_created).toLocaleString()}
                        </p>
                        <DeviceStatusBadge device={device} />
                      </div>
                    </div>
                    <div
                      className="flex-shrink-0"
                      onClick={(event) => event.stopPropagation()}
                      onMouseDown={(event) => event.stopPropagation()}
                    >
                      <ActionMenu
                        entityType="device"
                        entity={device}
                        menuKey={`${groupRowKey}:${device.id}`}
                        openMenus={deviceRowIdEllipsisOpen}
                        setOpenMenus={setDeviceRowIdEllipsisOpen}
                        onEdit={openEditFormForSystemOrDevice}
                        onRemove={prepareSystemOrDeviceForDeletion}
                      />
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="py-3 text-muted">No devices in this type.</div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderSystem = (systemNode, systemRowKey) => {
    // Empty for basic systems, which consume no system license.
    const systemLicenseLabel = getSystemLicenseLabel(
      systemNode.sourceSystem,
      licenseInfo,
      systemIndexMap,
    );

    return (
      <div key={systemNode.id} className="bg-base rounded-2 overflow-hidden">
        <div className="d-flex align-items-start justify-content-between gap-3 px-3 py-3 border-bottom border-secondary">
          <div className="d-flex align-items-start gap-3 min-w-0">
            <img
              src={getSystemIcon(systemNode.sourceSystem.system_type)}
              alt={systemNode.sourceSystem.system_type}
              width={'30px'}
              className="flex-shrink-0"
            />
            <div className="min-w-0">
              <p className="mb-1 fw-bold">{systemNode.name}</p>
              {hasDisplayValue(getSystemDisplayId(systemNode.sourceSystem)) && (
                <p className="mb-1">ID: {getSystemDisplayId(systemNode.sourceSystem)}</p>
              )}
              <p className="mb-1 text-light opacity-75">
                {systemNode.sourceSystem.email || 'webops@rescalert.com'}
              </p>
              {systemLicenseLabel && (
                <p className="mb-0 text-secondary">License Info: {systemLicenseLabel}</p>
              )}
            </div>
          </div>
          <div
            className="flex-shrink-0"
            onClick={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <ActionMenu
              entityType="system"
              entity={systemNode.sourceSystem}
              menuKey={systemRowKey}
              openMenus={systemRowIdEllipsisOpen}
              setOpenMenus={setSystemRowIdEllipsisOpen}
              onEdit={openEditFormForSystemOrDevice}
              onRemove={prepareSystemOrDeviceForDeletion}
            />
          </div>
        </div>
        <div className="d-flex flex-column gap-2 p-2">
          {systemNode.deviceGroups.map((deviceGroup) =>
            renderDeviceGroup(deviceGroup, systemRowKey),
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      {connectedSystemsHierarchy?.map((category, index) => (
        <div
          className={index !== connectedSystemsHierarchy.length - 1 ? 'mb-3' : ''}
          key={category.id}
        >
          <BaseAccordion
            bodyBackground="#325277"
            header={
              <div className="d-flex align-items-center gap-3 min-w-0">
                <img
                  src={getCategoryIcon(category.name)}
                  alt={category.name}
                  width={'30px'}
                  className="flex-shrink-0"
                />
                <div className="min-w-0">
                  <p className="fw-bold">{category.name}</p>
                  <p>
                    {category.systemsCount} {category.systemsCount === 1 ? 'system' : 'systems'} /{' '}
                    {category.devicesCount} {category.devicesCount === 1 ? 'device' : 'devices'}
                  </p>
                </div>
              </div>
            }
          >
            <div className="d-flex flex-column gap-3">
              {category.types.map((type) => (
                <div key={type.id} className="d-flex flex-column gap-2">
                  <div className="d-flex align-items-center justify-content-between gap-3 px-1">
                    <div className="fw-bold">{type.name}</div>
                    <div className="text-light opacity-75 flex-shrink-0">
                      {type.devicesCount} {type.devicesCount === 1 ? 'device' : 'devices'}
                    </div>
                  </div>
                  <div className="d-flex flex-column gap-2">
                    {type.systems.map((systemNode) =>
                      renderSystem(systemNode, `${category.id}:${type.id}:${systemNode.id}`),
                    )}
                  </div>
                </div>
              ))}
            </div>
          </BaseAccordion>
        </div>
      ))}
    </>
  );
};

export default Accordion;
